import { useMemo, useState, type ReactElement } from 'react';
import { Link, redirect, useLoaderData } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Badge, EmptyState } from '@marketplace/ui';
import {
  type JobFeedItem,
  type PayoutStatus,
  type ProviderSubscription,
} from '@marketplace/contracts';
import { localeOf } from '../features/auth/actions.js';
import { ApiError } from '../shared/api.js';
import {
  FEED_SORTS,
  NO_FILTERS,
  filterFeed,
  sortFeed,
  tradesIn,
  type FeedFilters,
  type FeedSort,
} from '../features/jobs/feed.js';
import { MAX_FEED_JOBS, loadJobFeed } from '../features/jobs/feed-query.js';
import { FeedBudget, FeedCoverage, FeedWhere } from '../features/jobs/FeedJobFacts.js';
import { loadMySubscription } from '../features/billing/billing.js';
import {
  SubscriptionRequiredBanner,
  isSubscriptionPending,
} from '../features/billing/SubscriptionRequiredBanner.js';
import { PayoutsPendingBanner } from '../features/payouts/PayoutsPendingBanner.js';
import { loadMyPayouts } from '../features/payouts/onboarding.js';
import { loadSession } from '../shared/session.js';

/**
 * The market, as a professional reads it — `W4-T07` §3.2, §3.3.
 *
 * **This slice's page, not `W12`'s** (`MEM-2026-09-20-31`): ADR-011 gives `agent-ui` the storefront —
 * the public, indexable pages a visitor reaches without an account — and an authenticated area over
 * one slice's own shapes is built by the slice that owns them, which is what `W2-T09` did with
 * `/account` and `W4-T04` with `/jobs`.
 *
 * **The loader assembles the whole bounded set rather than one page**, for `job.tsx`'s reason: the API
 * pages because the list is written by strangers in numbers nobody controls, and the screen assembles
 * because it sorts and filters (§2.6). Those are the same requirement seen from two ends. What it
 * cannot do is filter a job it has not loaded, so the bound is rendered rather than hidden.
 *
 * **The quote form is one click away, not here.** `W4-T07` left rows linking nowhere because nothing
 * could deliver; `W4-T10` built `/feed/:id`, and each row's title now goes there.
 */

interface FeedData {
  locale: string;
  jobs: JobFeedItem[];
  /** True when the market holds more than this screen will load. Rendered, never hidden. */
  truncated: boolean;
  /**
   * Why the API refused, when it did — and neither refusal is an error on this page.
   *
   * `profile` is a `409` (§2.7): the most likely first visit, by somebody whose signup is half
   * finished. `role` is a `403` (`W4-T10` §2.6): a client who followed the header's link to a page
   * for professionals. Both are loader *data*, rendered as what they mean.
   */
  blocked: 'profile' | 'role' | null;
  /**
   * `W5-T01` §4.3 — whether the provider can quote yet, for the banner. `null` when it could not be
   * read, which is no banner and never an error: the read is not what this page is for.
   */
  payouts: PayoutStatus | null;
  /**
   * `W5-T13` §4.6 — whether the provider may quote at all, the step before Stripe's. `null` when it
   * could not be read: no banner, as for `payouts`.
   */
  subscription: ProviderSubscription | null;
}

export async function loader({ params, context }: LoaderFunctionArgs): Promise<FeedData> {
  const locale = localeOf(params);

  // The guard `/account` established: this page has no content without a user, so an empty shell
  // saying "you are not signed in" would be a worse login page than the one that exists.
  const session = await loadSession(context);
  if (session === null) throw redirect(`/${locale}/login`);

  // In parallel with the feed; `loadMyPayouts` never rejects, so it cannot fail the page.
  const payouts = loadMyPayouts(context);
  const subscription = loadMySubscription(context, { fresh: false });
  try {
    const collected = await loadJobFeed(context);

    return {
      locale,
      jobs: collected.slice(0, MAX_FEED_JOBS),
      truncated: collected.length > MAX_FEED_JOBS,
      blocked: null,
      payouts: await payouts,
      subscription: await subscription,
    };
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) {
      return {
        locale,
        jobs: [],
        truncated: false,
        blocked: 'profile',
        payouts: null,
        subscription: null,
      };
    }
    if (error instanceof ApiError && error.status === 403) {
      return {
        locale,
        jobs: [],
        truncated: false,
        blocked: 'role',
        payouts: null,
        subscription: null,
      };
    }
    throw error;
  }
}

export function Component(): ReactElement {
  const { t } = useTranslation();
  const { locale, jobs, truncated, blocked, payouts, subscription } = useLoaderData<FeedData>();

  // Presentation state, and the only state on this page: how the provider is reading.
  const [sort, setSort] = useState<FeedSort>('newest');
  const [filters, setFilters] = useState<FeedFilters>(NO_FILTERS);

  const trades = useMemo(() => tradesIn(jobs), [jobs]);
  const shown = useMemo(() => sortFeed(filterFeed(jobs, filters), sort), [jobs, filters, sort]);

  if (blocked === 'role') {
    return (
      <article data-testid="feed">
        <h1>{t('feed.title')}</h1>
        <div data-testid="feed-for-pros">
          <EmptyState title={t('feed.forPros.title')} description={t('feed.forPros.body')} />
          <p>
            <Link to={`/${locale}/jobs`}>{t('feed.forPros.link')}</Link>
          </p>
        </div>
      </article>
    );
  }

  if (blocked === 'profile') {
    return (
      <article data-testid="feed">
        <h1>{t('feed.title')}</h1>
        <div data-testid="feed-blocked">
          <EmptyState title={t('feed.blocked.title')} description={t('feed.blocked.body')} />
          <p>
            <Link to={`/${locale}/account`}>{t('feed.blocked.link')}</Link>
          </p>
        </div>
      </article>
    );
  }

  return (
    <article data-testid="feed">
      <h1>{t('feed.title')}</h1>
      <p className="mp-lead">{t('feed.intro')}</p>
      {/* `W2-T05` §7: trades and radius are what this list is computed from, so the way to change
          them is one click from it. */}
      <p>
        <Link to={`/${locale}/account/pro`}>{t('feed.editProfile')}</Link>
      </p>
      {/* `W4-T09` (E8): a job won leaves the feed, so the way to it starts here. */}
      <p>
        <Link to={`/${locale}/work`}>{t('handshake.work.title')}</Link>
      </p>
      {/* `W5-T01` §4.3: above the list, not instead of it. The jobs are still there to read.
          `W5-T13` §4.6: the subscription is the earlier step; one pending step at a time. */}
      <SubscriptionRequiredBanner subscription={subscription} locale={locale} from="quote" />
      {isSubscriptionPending(subscription) ? null : (
        <PayoutsPendingBanner payouts={payouts} locale={locale} from="quote" />
      )}

      {/* The market is empty. A different sentence from "your filters emptied it" (§3.3), and the
          controls are not rendered at all: there is nothing to filter. */}
      {jobs.length === 0 ? (
        <div data-testid="feed-empty-market">
          <EmptyState
            title={t('feed.empty.market.title')}
            description={t('feed.empty.market.body')}
          />
        </div>
      ) : (
        <>
          <section className="mp-section" aria-labelledby="feed-controls">
            {/* Its own name, not the sort's: a section labelled "Ordenar por" and a control labelled
                the same thing are two things with one accessible name. */}
            <h2 id="feed-controls" className="mp-visually-hidden">
              {t('feed.filters.title')}
            </h2>

            <label htmlFor="feed-sort">{t('feed.sort.label')}</label>
            <select
              id="feed-sort"
              value={sort}
              onChange={(event) => setSort(event.target.value as FeedSort)}
            >
              {FEED_SORTS.map((option) => (
                <option key={option} value={option}>
                  {t(`feed.sort.${option}`)}
                </option>
              ))}
            </select>

            <label htmlFor="feed-trade">{t('feed.trade.label')}</label>
            <select
              id="feed-trade"
              value={filters.trade}
              onChange={(event) => setFilters({ ...filters, trade: event.target.value })}
            >
              <option value="all">{t('feed.trade.all')}</option>
              {trades.map((trade) => (
                <option key={trade.slug} value={trade.slug}>
                  {locale.startsWith('en') ? trade.nameEn : trade.nameEs}
                </option>
              ))}
            </select>

            {/* The charter's "licence gating", as a switch the professional flips (§2.2). */}
            <label htmlFor="feed-hide-licence">
              <input
                id="feed-hide-licence"
                type="checkbox"
                checked={filters.hideLicenceGaps}
                onChange={(event) =>
                  setFilters({ ...filters, hideLicenceGaps: event.target.checked })
                }
              />{' '}
              {t('feed.filter.licence')}
            </label>

            <label htmlFor="feed-hide-quoted">
              <input
                id="feed-hide-quoted"
                type="checkbox"
                checked={filters.hideQuoted}
                onChange={(event) => setFilters({ ...filters, hideQuoted: event.target.checked })}
              />{' '}
              {t('feed.filter.quoted')}
            </label>
          </section>

          <p className="mp-meta" data-testid="feed-count">
            {t('feed.count', { count: shown.length })}
          </p>
          {truncated ? (
            <p className="mp-meta">{t('feed.truncated', { count: MAX_FEED_JOBS })}</p>
          ) : null}

          {/* The filters emptied it. The controls above are still rendered, so it can be undone. */}
          {shown.length === 0 ? (
            <div data-testid="feed-empty-filtered">
              <EmptyState
                title={t('feed.empty.filtered.title')}
                description={t('feed.empty.filtered.body')}
              />
            </div>
          ) : (
            <ul className="mp-list" data-testid="feed-list">
              {shown.map((item) => (
                <li key={item.id} className="mp-section" data-testid={`feed-job-${item.id}`}>
                  <h2>
                    <Link to={`/${locale}/feed/${item.id}`}>
                      {item.title ?? t('jobs.untitled')}
                    </Link>
                  </h2>
                  {item.auction === null ? null : (
                    // `W6-T06`: an auction says so, with its deadline and ceiling — the two things a
                    // provider needs before deciding to open it. Nothing about other bids. Since
                    // FRONTEND §3.1 the "Subasta" word is the Badge; the facts stay plain text.
                    <p className="mp-meta" data-testid={`feed-auction-${item.id}`}>
                      <Badge tone="accent">{t('auction.label')}</Badge>{' '}
                      <strong>
                        {t('feed.auction', {
                          date: new Date(item.auction.closesAt).toLocaleString(locale, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          }),
                        })}
                        {item.auction.maxPriceCents === null
                          ? ''
                          : ` · ${t('auction.maxPrice', {
                              amount: (item.auction.maxPriceCents / 100).toLocaleString(locale, {
                                style: 'currency',
                                currency: 'EUR',
                              }),
                            })}`}
                      </strong>
                    </p>
                  )}
                  <FeedWhere item={item} locale={locale} />
                  {item.description === null ? null : <p>{item.description}</p>}
                  <FeedBudget item={item} locale={locale} />
                  <FeedCoverage item={item} locale={locale} />
                  {item.full ? (
                    // `W5-T08` §4.5: a label, in the neutral tone — a full job is not a fault, and the
                    // row stays listed and linked (E4 = A).
                    <p className="mp-meta" data-testid={`feed-full-${item.id}`}>
                      <Badge tone="neutral">{t('feed.full')}</Badge>
                    </p>
                  ) : null}
                  {item.myQuote === null ? null : (
                    <p data-testid={`feed-quoted-${item.id}`}>
                      {/* A quote that is no longer active freed the slot, and saying so is the whole
                          reason `myQuote.status` runs through `quoteStatusOf` (§2.8). */}
                      {item.myQuote.status === 'PENDING' || item.myQuote.status === 'ACCEPTED' ? (
                        <Badge tone="success">{t('feed.quoted')}</Badge>
                      ) : (
                        <Badge tone="neutral">{t('feed.quoted.again')}</Badge>
                      )}
                    </p>
                  )}
                  <p className="mp-meta">
                    {t('feed.published', {
                      date: new Date(item.publishedAt).toLocaleDateString(locale),
                    })}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </article>
  );
}
